import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

import {
    fetchTodayTasks,
    reorderTodayTasks,
    reorderTasksLocal,
    syncCardCompletion,
    carryOverTasks,
    fetchYesterdayCount,
    addTodayTask,
    fetchSuggestions
} from '../../redux/Slices/todayTaskSlice';
import { config } from '../../config';
import { fetchWithCSRF } from '../../utils/apiUtils';
import MyDayTaskItem from '../Workspaces/MyDayTaskItem';
import MyDay from '../icons/MyDay';
import Add from '../icons/Add';
import './MyDayColumn.scss';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatEstimate = (minutes) => {
    if (!minutes) return null;
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const MyDayColumn = () => {
    const dispatch = useDispatch();
    const { tasks, loading, yesterdayCount, suggestions, suggestionsLoading } = useSelector((state) => state.todayTask);
    const { board } = useSelector((state) => state.board);
    const boardId = board?.shortId;
    const [carryingOver, setCarryingOver] = useState(false);
    const [quickAddTitle, setQuickAddTitle] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const dayName = DAYS[now.getDay()];
    const dateStr = `${MONTHS[now.getMonth()]} ${now.getDate()}`;
    const carryOverKey = `myday_carryover_${boardId}_${today}`;
    const [carryOverDismissed, setCarryOverDismissed] = useState(
        () => sessionStorage.getItem(carryOverKey) === '1'
    );

    useEffect(() => {
        if (boardId) {
            dispatch(fetchTodayTasks({ boardId, date: today }));
            dispatch(fetchYesterdayCount({ boardId }));
        }
    }, [boardId, dispatch, today]);

    // Sync when card completion changes from the board
    useEffect(() => {
        const handleSync = (e) => {
            dispatch(syncCardCompletion(e.detail));
        };
        window.addEventListener('cardCompletionChanged', handleSync);
        return () => window.removeEventListener('cardCompletionChanged', handleSync);
    }, [dispatch]);

    useEffect(() => {
        return monitorForElements({
            onDrop({ source, location }) {
                if (source.data.type !== 'MY_DAY_TASK') return;

                const target = location.current.dropTargets[0];
                if (!target) return;

                const sourceId = source.data.taskId;
                const targetId = target.data.taskId;
                if (sourceId === targetId) return;

                const edge = extractClosestEdge(target.data);
                handleReorder(sourceId, targetId, edge);
            },
        });
    }, [tasks]);

    const handleReorder = useCallback((sourceTaskId, targetTaskId, edge) => {
        const currentTasks = [...tasks];
        const sourceIndex = currentTasks.findIndex(t => t._id === sourceTaskId);
        const targetIndex = currentTasks.findIndex(t => t._id === targetTaskId);

        if (sourceIndex === -1 || targetIndex === -1) return;

        const [movedTask] = currentTasks.splice(sourceIndex, 1);

        let insertIndex = currentTasks.findIndex(t => t._id === targetTaskId);
        if (edge === 'bottom') {
            insertIndex += 1;
        }

        currentTasks.splice(insertIndex, 0, movedTask);

        dispatch(reorderTasksLocal(currentTasks));

        const orderedIds = currentTasks.map(t => t._id);
        dispatch(reorderTodayTasks(orderedIds));
    }, [tasks, dispatch]);

    const dismissCarryOver = () => {
        setCarryOverDismissed(true);
        sessionStorage.setItem(carryOverKey, '1');
    };

    const handleCarryOver = async () => {
        setCarryingOver(true);
        await dispatch(carryOverTasks({ boardId }));
        setCarryingOver(false);
        dismissCarryOver();
    };

    // Quick-add: create card + add to My Day
    const handleQuickAdd = async (e) => {
        e.preventDefault();
        const title = quickAddTitle.trim();
        if (!title || !boardId || isAdding) return;

        setIsAdding(true);
        try {
            // Get first list for the board
            const listsRes = await fetchWithCSRF(
                `${config.API_URI}/v1/boards/${boardId}/lists`,
                { credentials: 'include' }
            );
            const listsData = await listsRes.json();
            const lists = listsData.lists || listsData.data || listsData;
            const listArr = Array.isArray(lists) ? lists : Object.values(lists);
            if (listArr.length === 0) return;

            // Create card
            const cardRes = await fetchWithCSRF(
                `${config.API_URI}/v1/cards`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title, actionListId: listArr[0]._id })
                }
            );
            const cardData = await cardRes.json();
            const card = cardData.card || cardData.data || cardData;

            // Add to My Day
            await dispatch(addTodayTask({ cardId: card._id, boardId }));

            setQuickAddTitle('');
        } catch (err) {
            console.error('Quick add failed:', err);
        } finally {
            setIsAdding(false);
        }
    };

    // Suggestions toggle
    const handleToggleSuggestions = () => {
        if (!showSuggestions && boardId) {
            dispatch(fetchSuggestions({ boardId }));
        }
        setShowSuggestions(!showSuggestions);
    };

    const handleAddSuggestion = (card) => {
        dispatch(addTodayTask({ cardId: card._id, boardId }));
    };

    const completedCount = tasks.filter(t => t.completed).length;
    const totalCount = tasks.length;
    const totalEstimate = tasks.reduce((sum, t) => sum + (t.estimate || 0), 0);

    return (
        <div className="myday-column">
            <div className="myday-date-header">
                <span className="myday-date-day">{dayName}</span>
                <span className="myday-date-full">{dateStr}</span>
                {totalEstimate > 0 && (
                    <span className="myday-total-estimate">{formatEstimate(totalEstimate)}</span>
                )}
            </div>

            {yesterdayCount > 0 && !carryOverDismissed && (
                <div className="myday-carryover-banner">
                    <span>
                        {yesterdayCount} unfinished task{yesterdayCount > 1 ? 's' : ''} from yesterday
                    </span>
                    <button
                        className="myday-carryover-btn"
                        onClick={handleCarryOver}
                        disabled={carryingOver}
                    >
                        {carryingOver ? 'Adding...' : 'Carry over'}
                    </button>
                    <button className="myday-carryover-dismiss" onClick={dismissCarryOver} title="Dismiss">
                        &times;
                    </button>
                </div>
            )}

            {totalCount > 0 && (
                <div className="myday-column-summary">
                    <div className="myday-progress-bar">
                        <div
                            className="myday-progress-fill"
                            style={{ width: `${(completedCount / totalCount) * 100}%` }}
                        />
                    </div>
                    <span className="myday-progress-text">
                        {completedCount} of {totalCount} completed
                    </span>
                </div>
            )}

            {loading && tasks.length === 0 ? (
                <div className="myday-loading">
                    <div className="myday-skeleton" />
                    <div className="myday-skeleton" />
                    <div className="myday-skeleton" />
                </div>
            ) : tasks.length === 0 ? (
                <div className="myday-empty-state">
                    <MyDay size={40} color="var(--secondary-text-color)" />
                    <p className="myday-empty-title">What's on your plate?</p>
                    <p className="myday-empty-desc">
                        Hover over any card and click the <strong>My Day</strong> icon to add it here.
                    </p>
                </div>
            ) : (
                <div className="myday-task-list">
                    {tasks.map((task) => (
                        <MyDayTaskItem
                            key={task._id}
                            task={task}
                            onReorder={handleReorder}
                        />
                    ))}
                </div>
            )}

            {/* Quick-add card */}
            <div className="myday-quick-add">
                <form onSubmit={handleQuickAdd}>
                    <div className="myday-quick-add-input">
                        <Add size={16} color="var(--secondary-text-color)" />
                        <input
                            type="text"
                            placeholder="Add a task..."
                            value={quickAddTitle}
                            onChange={(e) => setQuickAddTitle(e.target.value)}
                            disabled={isAdding}
                        />
                    </div>
                </form>
            </div>

            {/* Suggestions */}
            <div className="myday-suggestions-section">
                <button
                    className="myday-suggestions-toggle"
                    onClick={handleToggleSuggestions}
                >
                    <span>Suggestions</span>
                    <span className={`myday-suggestions-arrow ${showSuggestions ? 'open' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor">
                            <path d="M480-344 240-584l56-56 184 184 184-184 56 56-240 240Z"/>
                        </svg>
                    </span>
                </button>
                {showSuggestions && (
                    <div className="myday-suggestions-list">
                        {suggestionsLoading ? (
                            <div className="myday-suggestion-loading">Loading...</div>
                        ) : suggestions.length === 0 ? (
                            <div className="myday-suggestion-empty">No suggestions — you're all caught up</div>
                        ) : (
                            suggestions.map(card => (
                                <div
                                    key={card._id}
                                    className="myday-suggestion-item"
                                    onClick={() => handleAddSuggestion(card)}
                                >
                                    <div className="myday-suggestion-content">
                                        <span className="myday-suggestion-title">{card.title}</span>
                                        <span className="myday-suggestion-meta">
                                            {card.actionList?.title || card.actionListTitle || ''}
                                        </span>
                                    </div>
                                    <Add size={16} color="var(--brand-color)" />
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyDayColumn;
